import { useTenant } from "hooks/settings";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import React, { createContext, use, useMemo, useState } from "react";
import {
  InventoryEstimate,
  InventoryBreakdown,
  InventoryMeasurementItem,
  useCreateInventoryMeasurementSetMutation,
  useGetInventoryEstimateAtTimestampForMeasurementQuery,
  useGetInventoryBreakdownAtTimestampQuery,
  useCreateAndRunAutoRecipeMutation,
} from "src/store/api/generatedApi";
import {
  InventoryMeasurementFormData,
  InventoryMeasurementFormState,
  InventoryMeasurementSubmittedItem,
  DeliveryMeasurementSubmittedItem,
} from "../types";

interface InventoryMeasurementWithLocationsContextProps {
  inventoryEstimate: InventoryEstimate | undefined;
  inventoryBreakdown: InventoryBreakdown | undefined;
  inventoryMeasurementFormData: InventoryMeasurementFormData;
  adjustQuantity: (
    materialName: string,
    adjustedQuantity: number | null | undefined
  ) => void;
  adjustYardQuantity: (
    materialName: string,
    adjustedQuantity: number | null | undefined
  ) => void;
  adjustRailcarQuantity: (
    materialName: string,
    adjustedQuantity: number | null | undefined
  ) => void;
  formFilled: boolean;
  handleSubmit: () => Promise<void>;
  inventoryMeasurementFormState: InventoryMeasurementFormState;
  submittedItems: InventoryMeasurementSubmittedItem[] | undefined;
  submittedDeliveryItems: DeliveryMeasurementSubmittedItem[] | undefined;
  submittedMeasurementSetId: number | undefined;
  rollMillQuantity: number;
  setRollMillQuantity: (value: number) => void;
}

const InventoryMeasurementWithLocationsContext = createContext<
  InventoryMeasurementWithLocationsContextProps | undefined
>(undefined);

export const InventoryMeasurementWithLocationsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const tenantName = useTenant();
  const tenantData = useTenantData();
  const { t } = useTenantTranslation();

  const nowIso = useMemo(() => new Date().toISOString(), []);

  const { data: inventoryEstimate } =
    useGetInventoryEstimateAtTimestampForMeasurementQuery({
      tenantName,
      materialMetadataSetId: "default",
      materialPhysicsSetId: "default",
      timestamp: nowIso,
    });

  const { data: inventoryBreakdown } = useGetInventoryBreakdownAtTimestampQuery(
    {
      tenantName,
      timestamp: nowIso,
    }
  );

  const [inventoryMeasurementFormState, setInventoryMeasurementFormState] =
    useState(InventoryMeasurementFormState.Pending);

  const [updateInventoryMeasurement] =
    useCreateInventoryMeasurementSetMutation();

  const [createAndRunAutoRecipe] = useCreateAndRunAutoRecipeMutation();

  const [inventoryMeasurementFormData, setInventoryMeasurementFormData] =
    useState<InventoryMeasurementFormData>({ items: [] });

  const [submittedItems, setSubmittedItems] = useState<
    InventoryMeasurementSubmittedItem[] | undefined
  >(undefined);
  const [submittedDeliveryItems, setSubmittedDeliveryItems] = useState<
    DeliveryMeasurementSubmittedItem[] | undefined
  >(undefined);
  const [submittedMeasurementSetId, setSubmittedMeasurementSetId] = useState<
    number | undefined
  >(undefined);

  const [rollMillQuantity, setRollMillQuantity] = useState(0);

  // eslint-disable-next-line @eslint-react/exhaustive-deps -- adjustQuantity is intentionally not wrapped in useCallback; it only calls setInventoryMeasurementFormData which is stable
  const adjustQuantity = (
    materialName: string,
    parsedQuantity: number | null | undefined
  ) => {
    setInventoryMeasurementFormData((prev) => {
      const itemIndex = prev.items.findIndex(
        (item) => item.material_name === materialName
      );
      if (itemIndex !== -1) {
        if (parsedQuantity === undefined || parsedQuantity === null) {
          const existingItem = prev.items[itemIndex];
          if (!existingItem) return prev;
          if (
            existingItem.adjusted_yard_quantity === undefined &&
            existingItem.adjusted_railcar_quantity === undefined
          ) {
            return {
              ...prev,
              items: prev.items.filter(
                (item) => item.material_name !== materialName
              ),
            };
          }
          return {
            ...prev,
            items: prev.items.map((item, idx) =>
              idx === itemIndex
                ? { ...item, adjusted_quantity: undefined }
                : item
            ),
          };
        }
        return {
          ...prev,
          items: prev.items.map((item, idx) =>
            idx === itemIndex
              ? { ...item, adjusted_quantity: parsedQuantity }
              : item
          ),
        };
      } else {
        if (parsedQuantity === undefined || parsedQuantity === null) {
          return prev;
        }
        return {
          ...prev,
          items: [
            ...prev.items,
            {
              material_name: materialName,
              adjusted_quantity: parsedQuantity,
            },
          ],
        };
      }
    });
  };

  // eslint-disable-next-line @eslint-react/exhaustive-deps -- adjustYardQuantity is intentionally not wrapped in useCallback; it only calls setInventoryMeasurementFormData which is stable
  const adjustYardQuantity = (
    materialName: string,
    parsedQuantity: number | null | undefined
  ) => {
    setInventoryMeasurementFormData((prev) => {
      const itemIndex = prev.items.findIndex(
        (item) => item.material_name === materialName
      );
      if (itemIndex !== -1) {
        if (parsedQuantity === undefined || parsedQuantity === null) {
          const existingItem = prev.items[itemIndex];
          if (!existingItem) return prev;
          if (
            existingItem.adjusted_quantity === undefined &&
            existingItem.adjusted_railcar_quantity === undefined
          ) {
            return {
              ...prev,
              items: prev.items.filter(
                (item) => item.material_name !== materialName
              ),
            };
          }
          return {
            ...prev,
            items: prev.items.map((item, idx) =>
              idx === itemIndex
                ? { ...item, adjusted_yard_quantity: undefined }
                : item
            ),
          };
        }
        return {
          ...prev,
          items: prev.items.map((item, idx) =>
            idx === itemIndex
              ? { ...item, adjusted_yard_quantity: parsedQuantity }
              : item
          ),
        };
      } else {
        if (parsedQuantity === undefined || parsedQuantity === null) {
          return prev;
        }
        return {
          ...prev,
          items: [
            ...prev.items,
            {
              material_name: materialName,
              adjusted_yard_quantity: parsedQuantity,
            },
          ],
        };
      }
    });
  };

  // eslint-disable-next-line @eslint-react/exhaustive-deps -- adjustRailcarQuantity is intentionally not wrapped in useCallback; it only calls setInventoryMeasurementFormData which is stable
  const adjustRailcarQuantity = (
    materialName: string,
    parsedQuantity: number | null | undefined
  ) => {
    setInventoryMeasurementFormData((prev) => {
      const itemIndex = prev.items.findIndex(
        (item) => item.material_name === materialName
      );
      if (itemIndex !== -1) {
        if (parsedQuantity === undefined || parsedQuantity === null) {
          const existingItem = prev.items[itemIndex];
          if (!existingItem) return prev;
          if (
            existingItem.adjusted_quantity === undefined &&
            existingItem.adjusted_yard_quantity === undefined
          ) {
            return {
              ...prev,
              items: prev.items.filter(
                (item) => item.material_name !== materialName
              ),
            };
          }
          return {
            ...prev,
            items: prev.items.map((item, idx) =>
              idx === itemIndex
                ? { ...item, adjusted_railcar_quantity: undefined }
                : item
            ),
          };
        }
        return {
          ...prev,
          items: prev.items.map((item, idx) =>
            idx === itemIndex
              ? { ...item, adjusted_railcar_quantity: parsedQuantity }
              : item
          ),
        };
      } else {
        if (parsedQuantity === undefined || parsedQuantity === null) {
          return prev;
        }
        return {
          ...prev,
          items: [
            ...prev.items,
            {
              material_name: materialName,
              adjusted_railcar_quantity: parsedQuantity,
            },
          ],
        };
      }
    });
  };

  const formFilled = useMemo(() => {
    return !!inventoryEstimate && !!inventoryBreakdown;
  }, [inventoryEstimate, inventoryBreakdown]);

  // eslint-disable-next-line @eslint-react/exhaustive-deps -- handleSubmit is intentionally not wrapped in useCallback; it captures current form state via closure
  const handleSubmit = async () => {
    if (!inventoryEstimate || !inventoryBreakdown) return;

    setInventoryMeasurementFormState(InventoryMeasurementFormState.Submitting);

    const allMaterials = new Set<string>();
    inventoryEstimate.items.forEach((item) =>
      allMaterials.add(item.material_name)
    );
    inventoryBreakdown.items.forEach((item) =>
      allMaterials.add(item.material_name)
    );

    const measurementMap = new Map(
      inventoryMeasurementFormData.items.map((item) => [
        item.material_name,
        item.adjusted_quantity,
      ])
    );

    const yardAdjustmentMap = new Map(
      inventoryMeasurementFormData.items
        .filter((item) => item.adjusted_yard_quantity !== undefined)
        .map((item) => [item.material_name, item.adjusted_yard_quantity])
    );

    const railcarAdjustmentMap = new Map(
      inventoryMeasurementFormData.items
        .filter((item) => item.adjusted_railcar_quantity !== undefined)
        .map((item) => [item.material_name, item.adjusted_railcar_quantity])
    );

    const breakdownMap = new Map<
      string,
      { yard: number; railcar: number; price: number }
    >();
    inventoryBreakdown.items.forEach((item) => {
      if (item.source !== "sap") return;

      if (!breakdownMap.has(item.material_name)) {
        breakdownMap.set(item.material_name, {
          yard: 0,
          railcar: 0,
          price: item.specific_price,
        });
      }
      const entry = breakdownMap.get(item.material_name);
      if (!entry) return;
      if (item.location === "Yard") entry.yard += item.quantity;
      if (item.location === "Rail Car") entry.railcar += item.quantity;
      if (item.specific_price) entry.price = item.specific_price;
    });

    const estimateMap = new Map(
      inventoryEstimate.items.map((item) => [
        item.material_name,
        { quantity: item.quantity ?? 0, price: item.specific_price ?? 0 },
      ])
    );

    const itemsToSubmit = Array.from(allMaterials)
      .map((materialName) => {
        const bayActual = measurementMap.get(materialName);
        const estimate = estimateMap.get(materialName) ?? {
          quantity: 0,
          price: 0,
        };
        const breakdown = breakdownMap.get(materialName) ?? {
          yard: 0,
          railcar: 0,
          price: 0,
        };

        const bayToUse = bayActual ?? estimate.quantity;
        const yardToUse = yardAdjustmentMap.get(materialName) ?? breakdown.yard;
        const railcarToUse =
          railcarAdjustmentMap.get(materialName) ?? breakdown.railcar;

        const total = bayToUse + yardToUse + railcarToUse;
        const price = breakdown.price || estimate.price;

        return {
          material_name: materialName,
          quantity: total,
          quantity_scrap_bay: bayToUse,
          quantity_yard: yardToUse,
          quantity_rail_car: railcarToUse,
          quantity_scrap_bay_estimate: estimate.quantity,
          price,
          total,
        };
      })
      .sort((a, b) => a.material_name.localeCompare(b.material_name));

    const deliveryItems = inventoryEstimate.items
      .filter((item) => (item.deliveries_since_previous ?? 0) > 0)
      .map((item) => ({
        material_name: item.material_name,
        quantity: item.deliveries_since_previous ?? 0,
        source: "sap",
      }));

    if (rollMillQuantity > 0) {
      deliveryItems.push({
        material_name: t("rollMill"),
        quantity: rollMillQuantity,
        source: "manual",
      });
    }

    try {
      const measurementSetId = await updateInventoryMeasurement({
        tenantName,
        inventoryMeasurementSetCreate: {
          items: itemsToSubmit.map((item): InventoryMeasurementItem => ({
            material_name: item.material_name,
            quantity: item.quantity,
            quantity_scrap_bay: item.quantity_scrap_bay,
            quantity_yard: item.quantity_yard,
            quantity_rail_car: item.quantity_rail_car,
            quantity_scrap_bay_estimate: item.quantity_scrap_bay_estimate,
          })),
          delivery_items: deliveryItems,
        },
      }).unwrap();

      setSubmittedItems(itemsToSubmit);
      setSubmittedDeliveryItems(deliveryItems);
      setSubmittedMeasurementSetId(measurementSetId);
      setInventoryMeasurementFormState(InventoryMeasurementFormState.Submitted);

      const additionalRecipeTypes =
        tenantData.inventory_form_recipe_fanout.includes("charging")
          ? tenantData.inventory_form_recipe_fanout.filter(
              (type) => type !== "charging"
            )
          : [];
      try {
        await Promise.all([
          createAndRunAutoRecipe({
            tenantName,
            autoRecipeCreate: { auto_recipe_type: "charging" },
          }).unwrap(),
          ...additionalRecipeTypes.map((type) =>
            createAndRunAutoRecipe({
              tenantName,
              autoRecipeCreate: { auto_recipe_type: type },
            })
              .unwrap()
              .catch(() => null)
          ),
        ]);
      } catch {
        // Primary recipe failed but measurement was saved successfully
      }
    } catch {
      setInventoryMeasurementFormState(InventoryMeasurementFormState.Pending);
    }
  };

  const value = useMemo(
    () => ({
      inventoryEstimate,
      inventoryBreakdown,
      inventoryMeasurementFormData,
      adjustQuantity,
      adjustYardQuantity,
      adjustRailcarQuantity,
      handleSubmit,
      inventoryMeasurementFormState,
      formFilled,
      submittedItems,
      submittedDeliveryItems,
      submittedMeasurementSetId,
      rollMillQuantity,
      setRollMillQuantity,
    }),
    [
      inventoryEstimate,
      inventoryBreakdown,
      inventoryMeasurementFormData,
      adjustQuantity,
      adjustYardQuantity,
      adjustRailcarQuantity,
      handleSubmit,
      inventoryMeasurementFormState,
      formFilled,
      submittedItems,
      submittedDeliveryItems,
      submittedMeasurementSetId,
      rollMillQuantity,
      setRollMillQuantity,
    ]
  );

  return (
    <InventoryMeasurementWithLocationsContext value={value}>
      {children}
    </InventoryMeasurementWithLocationsContext>
  );
};

export const useInventoryMeasurementWithLocations = () => {
  const context = use(InventoryMeasurementWithLocationsContext);
  if (!context) {
    throw new Error(
      "useInventoryMeasurementWithLocations must be used within a InventoryMeasurementWithLocationsProvider"
    );
  }
  return context;
};
